import { PrivilegeEnum } from '@/api/ApiClient';
import type { PrivilegeWithOperation } from '@/stores/authStore';
import { getCustomizingParentPrivileges, getCustomizingSubRoutes } from '@/lib/customizing/customizingNav';

/**
 * Configuration structure for application routes and navigation
 */
export interface RouteConfig {
  path: string; // URL path
  label: string; // Navigation label
  privileges?: PrivilegeWithOperation[]; // Required access privileges
  subRoutes?: RouteConfig[]; // Child routes
  dividerBefore?: boolean; // UI divider indicator
}

/**
 * Main navigation route configuration
 */
export const routes: RouteConfig[] = [
  {
    label: 'Home',
    path: '/',
    // Public route, no privileges required
  },
  {
    label: 'Projects',
    path: '/projects',
    // No privileges required - access control handled by withResourceAuth component using current user data
    dividerBefore: true,
  },
  {
    label: 'Samples',
    path: '/samples',
    privileges: [{ p: PrivilegeEnum.Sample }],
  },
  {
    label: 'Substrates',
    path: '/substrates',
    privileges: [{ p: PrivilegeEnum.Sample }],
  },
  {
    label: 'Sample Requests',
    path: '/sample-requests',
    privileges: [{ p: PrivilegeEnum.SampleRequest }],
  },
  {
    label: 'Shipping',
    path: '/shippings',
    privileges: [{ p: PrivilegeEnum.Shipping }],
  },

  {
    label: 'Reports',
    path: '/reports',
    privileges: [{ p: PrivilegeEnum.SampleReport }],
    subRoutes: [
      {
        label: 'Sample Reports',
        path: '/sample-reports',
        privileges: [{ p: PrivilegeEnum.SampleReport, op: 'execute' }],
      },
      {
        label: 'Configure Sample Reports',
        path: '/configure-sample-reports',
        privileges: [{ p: PrivilegeEnum.SampleReport, op: 'update' }],
      },
    ],
  },
  {
    label: 'Bulk',
    path: '/bulk',
    privileges: [{ p: PrivilegeEnum.BulkRawMaterial }],
    dividerBefore: true,
  },
  {
    label: 'Publications',
    path: '/publications',
    privileges: [{ p: PrivilegeEnum.Publication }],
    dividerBefore: true,
  },
  {
    label: 'Data Packages',
    path: '/data',
    privileges: [{ p: PrivilegeEnum.DataPackage }],
  },
  {
    label: 'Sample Metadata',
    path: '/sample-metadata',
    privileges: [{ p: PrivilegeEnum.SampleStatistics }],
  },
  {
    label: 'ReaxFF Parameter Files',
    path: '/reaxff',
    privileges: [{ p: PrivilegeEnum.SimulationDataSet }],
    dividerBefore: true,
  },
  {
    label: 'Customizing',
    path: '/customizing',
    dividerBefore: true,
    privileges: getCustomizingParentPrivileges(),
    subRoutes: getCustomizingSubRoutes(),
  },
  {
    label: 'Config',
    path: '/config',
    privileges: [{ p: PrivilegeEnum.ConfigureFileProcessing }],
    dividerBefore: true,
    subRoutes: [
      {
        label: 'File sources',
        path: '/config/file-sources',
        privileges: [{ p: PrivilegeEnum.ConfigureFileProcessing }],
      },
      {
        label: 'Auto-submit',
        path: '/config/auto-submit',
        privileges: [{ p: PrivilegeEnum.ConfigureFileProcessing }],
      },
      {
        label: 'Sample import',
        path: '/config/sample-import',
        privileges: [{ p: PrivilegeEnum.ConfigureFileProcessing }],
      },
      {
        label: 'Characterization upload',
        path: '/config/characterization-upload',
        privileges: [{ p: PrivilegeEnum.ConfigureFileProcessing }],
      },
    ],
  },
  {
    label: 'Monitors',
    path: '/monitors',
    privileges: [
      { p: PrivilegeEnum.AutoSubmit },
      { p: PrivilegeEnum.CharacterizationUpload },
      { p: PrivilegeEnum.SampleImport },
      { p: PrivilegeEnum.ImportMonitoring },
    ],
    subRoutes: [
      {
        label: 'Automatic Sample Submit',
        path: '/automatic-sample-submit',
        privileges: [{ p: PrivilegeEnum.AutoSubmit }],
      },
      {
        label: 'Characterization Upload',
        path: '/characterization-upload',
        privileges: [{ p: PrivilegeEnum.CharacterizationUpload }],
      },
      {
        label: 'Sample Import',
        path: '/sample-import',
        privileges: [{ p: PrivilegeEnum.SampleImport }],
      },
      {
        label: 'Import Monitor',
        path: '/import-monitor',
        privileges: [{ p: PrivilegeEnum.ImportMonitoring }],
      },
    ],
  },
  {
    label: 'Debug',
    path: '/debug',
    dividerBefore: true,
    privileges: [{ p: PrivilegeEnum.ApiKeys }],
  },
  {
    label: 'Visualizations',
    path: '/visualizations',
    dividerBefore: false,
    privileges: [{ p: PrivilegeEnum.ApiKeys }],
  },
];

/**
 * Route with privilege requirements
 */
export type ProtectedRoute = {
  path: string;
  privileges?: PrivilegeWithOperation[];
};

/**
 * Returns flattened list of routes requiring privileges
 */
export function getProtectedRoutes(): ProtectedRoute[] {
  const protectedRoutes: ProtectedRoute[] = [];

  function addRoute(route: RouteConfig) {
    if (route.privileges && route.path) {
      protectedRoutes.push({ path: route.path, privileges: route.privileges });
    }
    route.subRoutes?.forEach(addRoute);
  }

  routes.forEach(addRoute);
  return protectedRoutes;
}

/**
 * Finds route configuration for a given path
 */
export function getRouteConfig(path: string): RouteConfig | undefined {
  function findRoute(routes: RouteConfig[]): RouteConfig | undefined {
    for (const route of routes) {
      if (route.path === path) return route;
      if (route.subRoutes) {
        const subRoute = findRoute(route.subRoutes);
        if (subRoute) return subRoute;
      }
    }
  }

  return findRoute(routes);
}
